/**
 * Apa yang boleh berdiri di sumbu crossplot LRLC, dan garis yang dipasang padanya.
 *
 * Dua hal yang kebetulan berdampingan di satu berkas, dan keduanya memang satu
 * urusan: **apa yang digambar**, dipisahkan dari **bagaimana menggambarnya**.
 * Ruangnya ruang nilai, bukan piksel — pemanggilnya yang tahu seberapa besar
 * kotaknya.
 *
 * Garis di sini selalu garis pada `log10(y)`, bukan pada y. Resistivitas
 * membentang beberapa dekade, dan garis lurus pada skala linear dikuasai
 * segelintir sampel terbesarnya; itu berlaku sama bagi garis yang dipasang
 * kuadrat terkecil di server maupun garis yang digeser orang dengan tangannya
 * sendiri. Kedua garis karena itu ditulis dalam ejaan yang sama persis, dan
 * yang membedakannya hanyalah dari mana kemiringannya datang.
 */

import type { LrlcFit } from "../types";

/** Sebuah besaran yang boleh dipasang di sumbu, sebagaimana orang menyebutnya. */
export interface LrlcChannel {
  key: string;
  /** Nama pendek, yang tertulis di tepi sumbu. */
  label: string;
  /** Nama panjang, yang terbaca di daftar pilihan. */
  name: string;
  unit: string;
  /**
   * Benar untuk besaran yang dibaca per dekade.
   *
   * Bukan selera dan bukan pilihan pengguna: 2 ohm·m dan 20 ohm·m adalah dua
   * batuan yang berbeda, dan pada sumbu linier keduanya duduk di satu piksel
   * yang sama di tepi kiri kotak. Sebaliknya porositas 0,02 dan 0,20 memang
   * berjarak sejauh selisihnya, dan sumbu logaritmik untuknya meregangkan
   * batuan tanpa pori menjadi separuh gambar.
   */
  log: boolean;
}

/**
 * Kesepuluh besaran, cerminan `LRLC_AXES` di `petro/views.py`.
 *
 * Urutannya urutan yang dipajang daftar pilihan, dan itu urutan orang
 * membacanya: keenam kurva yang dibaca layar ini lebih dulu — dalam urutan
 * yang sama dengan rel kiri layarnya — lalu keempat besaran yang dihitungnya.
 *
 * Sebuah cerminan yang menyimpang tidak akan melempar. Ia akan memajang daftar
 * berisi sebuah besaran yang tidak pernah dikirim server, dan yang memilihnya
 * mendapat kotak kosong tanpa satu pun keterangan. Jadi kedua sisi dipatok uji
 * pada daftar yang sama persis — `lib/lrlcplot.test.ts` di sini dan
 * `test_lrlc_api.py` di sana — dan yang menggeser salah satunya akan melihat
 * dua uji gagal, bukan nol.
 */
export const LRLC_CHANNELS: LrlcChannel[] = [
  { key: "rt", label: "RT", name: "Deep resistivity", unit: "ohm·m", log: true },
  { key: "phie", label: "PHIE", name: "Effective porosity", unit: "v/v", log: false },
  { key: "phit", label: "PHIT", name: "Total porosity", unit: "v/v", log: false },
  { key: "vsh", label: "VSH", name: "Shale volume", unit: "v/v", log: false },
  { key: "rhob", label: "RHOB", name: "Bulk density", unit: "g/cc", log: false },
  { key: "nphi", label: "NPHI", name: "Neutron porosity", unit: "v/v", log: false },
  { key: "rwa", label: "RWA", name: "Apparent Rw — RT / F", unit: "ohm·m", log: true },
  { key: "ro", label: "RO", name: "Wet resistivity — F · Rw", unit: "ohm·m", log: true },
  { key: "f", label: "F", name: "Formation factor", unit: "", log: true },
  { key: "rt_ro", label: "RT / RO", name: "Resistivity above its water line", unit: "", log: true },
];

/**
 * Sumbu yang terpasang sebelum siapa pun memilih.
 *
 * RT mendatar dan PHIE tegak, sebab itulah crossplot yang menjawab pertanyaan
 * layar ini: sebuah lapisan berpori yang resistivitasnya rendah adalah persis
 * yang sedang dicari, dan pada gambar inilah ia berdiri terpisah dari serpih
 * rapat yang resistivitasnya juga rendah.
 */
export const DEFAULT_X = "rt";
export const DEFAULT_Y = "phie";

/** Besaran menurut kuncinya; yang tak dikenal memulangkan kunci itu sendiri. */
export function channelOf(key: string): LrlcChannel {
  return (
    LRLC_CHANNELS.find((channel) => channel.key === key)
    ?? { key, label: key.toUpperCase(), name: key, unit: "", log: false }
  );
}

/** Keterangan tepi sumbu: nama, satuan, dan penyebutan skalanya bila log. */
export function captionOf(channel: LrlcChannel): string {
  const unit = channel.unit ? ` ${channel.unit}` : "";
  return `${channel.label}${unit}${channel.log ? " · log" : ""}`;
}

/**
 * Garis lurus pada `log10(y) = slope · x + intercept`.
 *
 * Sengaja tidak menyimpan nilainya di x = 0 sebagai anggota ketiga. Nilai itu
 * adalah `10^intercept`, dan sebuah salinan yang disimpan di sebelahnya adalah
 * salinan yang bisa berselisih dengan garis yang digambar di atasnya — yaitu
 * tepat kekeliruan yang paling mahal di layar ini, sebab angka itulah yang
 * dipasang orang sebagai Rw.
 */
export interface Line {
  slope: number;
  intercept: number;
}

/** Garis dari hasil regresi server. */
export function lineOf(fit: LrlcFit): Line {
  return { slope: fit.slope, intercept: fit.intercept };
}

/** Nilai garis ini di x = 0 — batuan tanpa lempung, yaitu jawabannya. */
export function valueOf(line: Line): number {
  return 10 ** line.intercept;
}

/** Nilai garis ini di sebuah x. */
export function valueAtX(line: Line, x: number): number {
  return 10 ** (line.slope * x + line.intercept);
}

/** Garis dengan nilai yang diminta di x = 0, kemiringannya tetap. */
export function withValue(line: Line, value: number): Line | null {
  if (!(value > 0) || !Number.isFinite(value)) return null;
  return { slope: line.slope, intercept: Math.log10(value) };
}

/** Garis dengan kemiringan yang diminta, nilainya di x = 0 tetap. */
export function withSlope(line: Line, slope: number): Line | null {
  if (!Number.isFinite(slope)) return null;
  return { slope, intercept: line.intercept };
}

/**
 * Garis yang tetap tertambat di x = 0 dan lewat titik (x, y).
 *
 * Inilah yang dipakai pegangan kanan: yang diputar orang adalah kemiringannya,
 * dan perpotongan di x = 0 — yaitu jawabannya — tidak boleh ikut bergeser
 * ketika yang disentuh adalah ujung yang lain. Memulangkan `null` pada x yang
 * terlalu dekat ke nol: di sana kemiringan tidak ditentukan, dan garis yang
 * dihitung dari pembagian itu melompat ke kemiringan apa saja.
 */
export function pivotThrough(line: Line, x: number, y: number): Line | null {
  if (!(y > 0) || !Number.isFinite(x) || Math.abs(x) < 1e-6) return null;
  return { slope: (Math.log10(y) - line.intercept) / x, intercept: line.intercept };
}

/**
 * Sisa kuadrat rata-rata garis ini terhadap titik yang dipakainya, dalam dekade.
 *
 * Dihitung di layar dan atas titik yang **tergambar**, jadi ia bukan R² yang
 * dipulangkan server: yang di sana dihitung atas seluruh sampel, yang di sini
 * hanya atas yang lolos penjarangan. Keduanya karena itu tidak dipajang
 * sebagai satu angka yang sama. Gunanya bukan menjadi ukuran mutlak melainkan
 * menjadi **pembanding**: garis yang digeser dengan tangan dan garis kuadrat
 * terkecil diukur pada himpunan yang sama persis, sehingga selisih keduanya
 * mengatakan berapa banyak yang dilepas orang demi Rw yang dipilihnya.
 *
 * Dekade dan bukan ohm·m, sebab yang diregresi memang logaritmanya: 0,08
 * dekade berarti sampel biasanya meleset sekitar 20% dari garisnya, dan angka
 * itu berarti sama entah di 0,05 ohm·m atau di 50.
 */
export function misfit(
  x: readonly number[],
  y: readonly number[],
  used: readonly boolean[],
  line: Line,
): number | null {
  let sum = 0;
  let n = 0;
  for (let i = 0; i < x.length; i += 1) {
    if (!used[i]) continue;
    const xi = x[i];
    const yi = y[i];
    if (!Number.isFinite(xi) || !(yi > 0)) continue;
    const residual = Math.log10(yi) - (line.slope * xi + line.intercept);
    sum += residual * residual;
    n += 1;
  }
  return n ? Math.sqrt(sum / n) : null;
}
